/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useLayoutEffect, useState, type RefObject } from "react";

export type RowWindow = { start: number; end: number; padTop: number; padBottom: number };

/**
 * The nearest ancestor that scrolls vertically. A box that only scrolls sideways computes its
 * overflow-y as auto too, so the ancestor must also have more content than height.
 */
function scrollParentOf(element: HTMLElement | null): HTMLElement | null {
  for (let node = element?.parentElement ?? null; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
}

/**
 * The slice of equal-height rows worth rendering: the ones in the nearest scrolling ancestor's
 * viewport plus a few either side, with the space the rest would take kept as padding so the
 * scrollbar and any loader below stay honest. Without a measurable scroll parent (tests, print) or
 * a measured row height yet, every row renders.
 */
export function useVirtualRows({
  count,
  rowHeight,
  overscan = 8,
  anchor,
}: {
  count: number;
  rowHeight: number;
  overscan?: number;
  anchor: RefObject<HTMLElement | null>;
}): RowWindow {
  const [range, setRange] = useState({ start: 0, end: count });

  useLayoutEffect(() => {
    const element = anchor.current;
    const parent = scrollParentOf(element);
    if (!element || !parent || parent.clientHeight === 0 || rowHeight <= 0) {
      setRange({ start: 0, end: count });
      return;
    }
    let frame = 0;
    const measure = () => {
      frame = 0;
      const top =
        element.getBoundingClientRect().top - parent.getBoundingClientRect().top + parent.scrollTop;
      const first = Math.floor((parent.scrollTop - top) / rowHeight);
      const visible = Math.ceil(parent.clientHeight / rowHeight);
      const start = Math.max(0, first - overscan);
      const end = Math.min(count, Math.max(first, 0) + visible + overscan);
      setRange((previous) =>
        previous.start === start && previous.end === end ? previous : { start, end },
      );
    };
    const schedule = () => {
      if (!frame) {
        frame = requestAnimationFrame(measure);
      }
    };
    measure();
    parent.addEventListener("scroll", schedule, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    observer?.observe(parent);
    return () => {
      parent.removeEventListener("scroll", schedule);
      observer?.disconnect();
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [anchor, count, rowHeight, overscan]);

  const start = Math.min(range.start, count);
  const end = Math.min(range.end, count);
  return { start, end, padTop: start * rowHeight, padBottom: (count - end) * rowHeight };
}
